import type { Product } from '../data/products'

interface SpecSheetParams {
  product: Product
  categoryName: string
}

/**
 * Generates a printable, branded spec sheet for a product by opening a new
 * window with styled HTML. The user can print it or save it as a PDF using
 * their browser's print dialog (which auto-opens).
 *
 * Works fully client-side from existing product data — no external PDFs needed.
 */
export function generateSpecSheet({ product, categoryName }: SpecSheetParams) {
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const specsRows = product.specs
    ? [
        { label: 'Size / Gradation', value: product.specs.size },
        {
          label: 'Density (Weight per Yard)',
          value: product.specs.weightPerYard,
        },
        { label: 'Compaction Rating', value: product.specs.compaction },
        { label: 'Estimated Coverage', value: product.specs.coverage },
      ].filter((row) => !!row.value)
    : []

  const bestForChips =
    product.bestFor && product.bestFor.length > 0
      ? product.bestFor
          .map((tag) => `<span class="chip">${escapeHtml(tag)}</span>`)
          .join('')
      : ''

  const proTipBlock = product.proTip
    ? `
      <section class="pro-tip">
        <div class="pro-tip-label">Pro Tip</div>
        <p>${escapeHtml(product.proTip)}</p>
      </section>
    `
    : ''

  const priceBlock =
    product.pricePerYard > 0
      ? `<div class="price">$${product.pricePerYard}<span class="price-unit"> / yard</span></div>
         <div class="price-note">Price subject to change. Delivery fees apply based on zone.</div>`
      : `<div class="price-note">Custom pricing — call for a quote.</div>`

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(product.name)} — Spec Sheet | 253Gravel</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #1a1a1a;
    background: #ffffff;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }
  .page {
    max-width: 7.5in;
    margin: 0 auto;
    padding: 0.6in 0.6in 0.5in;
  }
  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border-bottom: 3px solid #059F40;
    padding-bottom: 14px;
    margin-bottom: 24px;
  }
  .brand {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #1a1a1a;
  }
  .brand-accent { color: #059F40; }
  .brand-tag {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #6b7280;
    margin-top: 4px;
  }
  .meta {
    text-align: right;
    font-size: 10px;
    color: #6b7280;
    line-height: 1.6;
  }
  .meta strong { color: #1a1a1a; font-weight: 700; }
  .category-pill {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #059F40;
    margin-bottom: 8px;
  }
  h1 {
    font-size: 30px;
    font-weight: 800;
    margin: 0 0 6px;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }
  .description {
    font-size: 13px;
    color: #374151;
    margin: 14px 0 22px;
  }
  .price-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-left: 4px solid #059F40;
    border-radius: 6px;
    padding: 14px 18px;
    margin-bottom: 24px;
  }
  .price-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: #6b7280;
  }
  .price {
    font-size: 24px;
    font-weight: 800;
    color: #1a1a1a;
    margin-top: 2px;
  }
  .price-unit { font-size: 13px; font-weight: 500; color: #6b7280; }
  .price-note {
    text-align: right;
    font-size: 10px;
    color: #9ca3af;
    max-width: 220px;
  }
  h2 {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #6b7280;
    margin: 22px 0 10px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e5e7eb;
  }
  .specs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .spec {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 12px;
  }
  .spec-label {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9ca3af;
    margin-bottom: 4px;
  }
  .spec-value {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a1a;
  }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 4px 10px;
  }
  .pro-tip {
    margin-top: 22px;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 6px;
    padding: 12px 14px;
  }
  .pro-tip-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #92400e;
    margin-bottom: 4px;
  }
  .pro-tip p { margin: 0; font-size: 12px; color: #1a1a1a; }
  .footer {
    margin-top: 30px;
    padding-top: 14px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #6b7280;
  }
  .footer strong { color: #1a1a1a; font-weight: 700; }
  .disclaimer {
    margin-top: 12px;
    font-size: 9px;
    color: #9ca3af;
    line-height: 1.5;
  }
  @media print {
    body { background: #fff; }
    .page { padding: 0.4in 0.5in; }
    @page { size: letter; margin: 0; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div>
        <div class="brand">253<span class="brand-accent">Gravel</span></div>
        <div class="brand-tag">Premier Aggregate &amp; Delivery · Tacoma, WA</div>
      </div>
      <div class="meta">
        <div><strong>Product Spec Sheet</strong></div>
        <div>Generated ${escapeHtml(today)}</div>
        <div>(206) 619-3742</div>
      </div>
    </div>

    <span class="category-pill">${escapeHtml(categoryName)}</span>
    <h1>${escapeHtml(product.name)}</h1>

    <p class="description">${escapeHtml(product.description)}</p>

    <div class="price-row">
      <div>
        <div class="price-label">Per Yard Price</div>
        ${priceBlock}
      </div>
    </div>

    ${
      specsRows.length > 0
        ? `
      <h2>Technical Specifications</h2>
      <div class="specs">
        ${specsRows
          .map(
            (row) => `
          <div class="spec">
            <div class="spec-label">${escapeHtml(row.label)}</div>
            <div class="spec-value">${escapeHtml(row.value || '')}</div>
          </div>
        `,
          )
          .join('')}
      </div>
    `
        : ''
    }

    ${
      bestForChips
        ? `
      <h2>Best For</h2>
      <div class="chips">${bestForChips}</div>
    `
        : ''
    }

    ${proTipBlock}

    <div class="footer">
      <div>
        <strong>253Gravel</strong> · Bonney Lake, WA<br />
        info@253gravel.com · 253gravel.com
      </div>
      <div style="text-align: right;">
        <strong>Order or quote:</strong><br />
        (206) 619-3742
      </div>
    </div>

    <p class="disclaimer">
      This spec sheet is generated from current product data and is intended for general reference. Coverage estimates assume even spread at the listed depth and may vary based on compaction and site conditions. For certified gradation curves, MSDS, or compaction test data, please call us at (206) 619-3742.
    </p>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 200);
    });
  </script>
</body>
</html>`

  const win = window.open(
    '',
    '_blank',
    'noopener,noreferrer,width=900,height=1100',
  )
  if (!win) {
    // Popup blocked — fallback to a download via blob URL
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slugify(product.name)}-spec-sheet.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    return
  }
  win.document.open()
  win.document.write(html)
  win.document.close()
}

function escapeHtml(input: string): string {
  return String(input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}
